import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ScriptLoaderService } from '../../core/helpers/script-loader.service';
import { debounceTime, Subject, Subscription, timeout } from 'rxjs';
import { SubscriptionService } from '../../core/subscription/subscription.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Business } from '../../core/business/business';
import { UserService } from '../../core/user/user.service';
import { Router } from '@angular/router';
import { SubscriptionData, SubscriptionPlan, SubscriptionPlanName, SubscriptionStatus, SubscriptionType, InvoicePreview, SubscriptionInterval } from '../../core/subscription/subscription';
import { AnalyticsEvent, InternalAnalyticsService } from '../../core/internal-analytics.service';
import { CustomErrorHandler } from '../../core/error-handler';
import { SessionService } from '../../core/session.service';
import { NgxSmartModalService } from 'ngx-smart-modal';
import * as Sentry from '@sentry/angular';
import { WebsiteTextingSubscriptionService } from '../../core/subscription/website-texting-subscription.service';

declare const Stripe;

@Component({
  selector: 'rosie-subscription',
  templateUrl: 'subscription.component.html',
  styleUrls: ['subscription.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})

export class SubscriptionComponent implements OnInit, OnDestroy {
  @ViewChild('cardElement') cardElement: ElementRef;
  @Output() onCancel = new Subject<boolean>();
  @Output() onInitialized = new EventEmitter<void>();
  stripe: any;
  mainStripeElement: any;
  stripeElements: any;

  cardholderForm: UntypedFormGroup;
  paymentError: string;
  inProgress: boolean;
  isInitializingPaymentMethod = true;
  stripeInitializationFailed = false;
  // True from modal open until the user refresh resolves the real card-on-file
  // state. While true the modal shows a loader instead of committing to the
  // card-entry vs plan-change layout (which both pivot on hasCreditCardOnFile).
  isResolvingCardState = false;
  invalidState = new Subject<boolean>();
  stripeElementInstances = [];
  threeDayExtensionDateForRenewal: any;
  shouldShowWebsiteTextingAddon = false;
  private subscriptions: Subscription[] = [];
  private stripeFieldSubscriptions: Subscription[] = [];
  private stripeInitialized = false;
  private stripeInitRetries = 0;
  private stripeInitTimeoutId: any;
  private baseCssClasses = new Set<string>([FormCssClasses.Control]);

  constructor(private scriptService: ScriptLoaderService, public subscription: SubscriptionService, private router: Router, private translate: TranslateService, private modal: NgxSmartModalService,
    private user: UserService, private formBuilder: UntypedFormBuilder, private analytics: InternalAnalyticsService, private error: CustomErrorHandler, private session: SessionService,
    private wtSubscriptionService: WebsiteTextingSubscriptionService) {
    this.threeDayExtensionDateForRenewal = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();
  }

  get websiteTextingSelected(): boolean {
    return this.subscription.websiteTextingPreSelected$.value;
  }

  get activeBusiness(): Business {
    return this.user.activeBusiness;
  }

  get plans() {
    return this.subscription.plans;
  }

  get hasCreditCardOnFile(): boolean {
    return this.user.hasCreditCardOnFile;
  }

  get isPaymentMethodInvalid() {
    return this.subscription.isPaymentMethodInvalid;
  }

  get isTrialing() {
    return this.subscription.userData?.subscription?.status === SubscriptionStatus.Trialing;
  }

  get initialPlan(): SubscriptionPlanName {
    return this.subscription.initialPlan;
  }

  get selectedPlan(): SubscriptionPlan {
    return this.subscription.selectedPlan;
  }

  get isDowngrade(): boolean {
    return this.subscription.isDowngrade;
  }

  get isCouponRevalidating(): boolean {
    return this.subscription.isRevalidatingCoupon;
  }

  get isUpgrade(): boolean {
    return this.subscription.isUpgrade;
  }

  get isUpgradeToPlanActive(): boolean {
    return this.subscription.isUpgradeToPlanActive;
  }

  get planRenewalDate(): string {
    return this.subscription.subscriptionData?.billingDate;
  }

  get trialEndDate(): string {
    return this.subscription.subscriptionData?.trialEndsAt;
  }

  get invoicePreview(): InvoicePreview {
    return this.subscription.invoicePreview;
  }

  get isLoadingInvoice() {
    return this.subscription.isLoadingInvoice;
  }

  get isEnterprisePlanSelected(): boolean {
    return this.subscription.isEnterprisePlanSelected
  }

  get hasOnlyIntervalChange(): boolean {
    return this.subscription.hasOnlyIntervalChange;
  }

  get hasAnyChange(): boolean {
    return this.subscription.hasAnyChange;
  }

  get isCanceled() {
    return this.subscription?.isCanceled;
  }

  get hasTrialExpired(): boolean {
    return this.user.hasTrialExpired;
  }

  get currentSubscriptionId(): string {
    return this.user.subscription?.id;
  }

  get getStartedTranslationKey(): string {
    return this.user.isUserOnFreeMinutesBillingVersion ? 'SUBSCRIPTION.START_TRIAL' : 'SUBSCRIPTION.GET_STARTED';
  }

  get isUserOn7DayFreeTrialVersion(): boolean {
    return this.user.isUserOn7DayFreeTrialVersion;
  }

  get isMobileVersion(): boolean {
    return this.session.isMobileVersion;
  }

  get isPlanMode(): boolean {
    return this.hasCreditCardOnFile && !this.isCanceled;
  }

  get isUserOnBillingV4(): boolean {
    return this.user.isUserOnBillingV4;
  }

  get isUserOnTrialBasedBilling(): boolean {
    return this.user.isUserOnTrialBasedBilling;
  }

  get isExternalBillingProvider(): boolean {
    return this.subscription.isExternalBillingProvider;
  }

  getLicensedPlanPrice(plan?: any): any {
    const selectedInterval = this.subscription.selectedInterval;
    const amount = plan?.prices?.find((price) => price.usageType === SubscriptionType.Licensed && price.interval === selectedInterval)?.amount;
    const formattedAmount = amount ? amount.toLocaleString() : '0';
    return `$${formattedAmount}${selectedInterval === SubscriptionInterval.Yearly ? ' /year' : ' /month'}`;
  }

  getSelectedPlanPrice(): string {
    if (this.subscription.selectedPlanDisplay?.price) {
      const isYearly = this.subscription.selectedInterval === SubscriptionInterval.Yearly;
      const intervalText = isYearly ? '/year' : '/month';

      // For yearly plans, show the annual billing amount instead of the monthly price
      if (isYearly && this.subscription.selectedPlanDisplay.annualBillingAmount) {
        return `$${this.subscription.selectedPlanDisplay.annualBillingAmount} ${intervalText}`;
      }

      return `${this.subscription.selectedPlanDisplay.price} ${intervalText}`;
    }
    return this.getLicensedPlanPrice(this.selectedPlan);
  }

  getBillingIntervalText(): string {
    return this.subscription.selectedInterval === SubscriptionInterval.Yearly ? '/year' : '/month';
  }

  getTermsOfServiceText(): string {
    // Check if there's a discounted price in the selected plan display
    let price = this.subscription.selectedPlanPrice;
    let hasDiscount = false;

    if (this.subscription.selectedPlanDisplay?.couponPreview) {
      const selectedInterval = this.subscription.selectedInterval;
      const targetInterval = selectedInterval === SubscriptionInterval.Yearly ? 'year' : 'month';

      // Try to find discounted price for the selected interval
      const discountedPriceInfo = this.subscription.selectedPlanDisplay.couponPreview.prices?.find(
        (cp) => cp.interval === targetInterval
      );

      if (discountedPriceInfo?.discountedAmount) {
        price = discountedPriceInfo.discountedAmount;
        hasDiscount = true;
      }
    }

    const formattedPrice = price % 1 === 0 ? price.toString() : price.toFixed(2);
    const billingInterval = this.getBillingIntervalText();

    let trialText = '';
    if (this.isUserOnTrialBasedBilling) {  // V3 OR V4
      const trialDays = this.hasTrialExpired ? 3 : (this.subscription.subscriptionData?.trialDays || 7);
      const trialEndDate = this.hasTrialExpired ? this.threeDayExtensionDateForRenewal : this.subscription.subscriptionData?.trialEndsAt;

      const effectiveDate = trialEndDate
        ? new Date(trialEndDate)
        : new Date(Date.now() + trialDays * 24 * 60 * 60 * 1000);

      const formattedDate = effectiveDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      trialText = `${trialDays}-day trial which ends ${formattedDate}`;
    } else {
      trialText = '25-minute trial ends';
    }

    let wtText = '';
    if (this.websiteTextingSelected) {
      wtText = `, plus monthly billing and overages for Website Texting,`;
    }

    return `Rosie is $${formattedPrice}${billingInterval}${wtText} starting after your ${trialText}. `;
  }

  getUpgradeButtonText(): string {
    if (this.hasOnlyIntervalChange) {
      const targetInterval = this.subscription.selectedInterval;
      return targetInterval === SubscriptionInterval.Yearly ? 'Switch to Annual Billing' : 'Switch to Monthly Billing';
    }

    if (this.isDowngrade) {
      return this.isTrialing ? 'Downgrade Now' : 'Confirm Downgrade';
    }
    return 'Upgrade Now';
  }

  onWebsiteTextingSelectedChange(value: boolean): void {
    this.subscription.websiteTextingPreSelected$.next(value);
  }

  ngOnInit(): void {
    this.cardholderForm = this.formBuilder.group({
      authorizePayment: [true, Validators.required],
      zip: ['', Validators.required]
    });

    this.subscriptions.push(
      this.subscription.initialize.pipe(debounceTime(500)).subscribe(() => this.initializeStripe()),
      this.subscription.reset.subscribe(() => this.clearForm()),
      this.session.refreshState.subscribe(() => {
        if (!this.inProgress && !this.isInitializingPaymentMethod) {
          this.refreshStripeFields();
        }
      }),
      this.subscription.open.subscribe(() => {
        this.shouldShowWebsiteTextingAddon = this.subscription.websiteTextingPreSelected$.value;
        // The Variant B registration card attaches asynchronously (queued backend
        // job), so a session-old user object can still read isCreditCardAdded=false
        // even though a card is already on file. Refresh on open so hasCreditCardOnFile
        // is accurate — otherwise the card form shows on a plan change and submit()
        // attaches a SECOND PaymentMethod for the same card (Stripe mints a new pm_
        // per confirmCardSetup; nothing dedups it). The modal opens instantly and
        // shows a loader for the brief refresh rather than committing to a layout it
        // would then visibly swap (card form -> plan-only) once the refresh lands.
        // Only block when we currently believe there is NO card on file — that's the
        // only state that can be harmfully stale (card actually exists -> would show
        // the card form and re-attach a duplicate). A card-on-file user already lands
        // on the correct plan-only layout, so they open instantly with no loader.
        this.isResolvingCardState = !this.hasCreditCardOnFile;
        // Safety net: Angular HttpClient has no default timeout, so a hung /user
        // request (socket open, no response, no error) would otherwise leave the
        // loader up forever. After 5s, give up on the refresh and reveal the body
        // with whatever state we have (today's behavior). timeout() also cancels the
        // request, so a late response can't swap the layout after the fallback.
        this.user.getUser().pipe(timeout({ first: 5000 })).subscribe({
          next: () => this.isResolvingCardState = false,
          error: () => this.isResolvingCardState = false
        });
      })
    );
  }

  ngOnDestroy(): void {
    this.refreshStripeFields();
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.stripeFieldSubscriptions.forEach(sub => sub.unsubscribe());
    this.invalidState.complete();
    this.onCancel.complete();
  }

  getErrorElId(fieldId: string): string {
    return `${fieldId}-error`;
  }

  cancel() {
    this.onCancel.next(true);
    this.subscription.close.next(true);
  }

  subscribe() {
    if (this.isExternalBillingProvider || this.isInitializingPaymentMethod || this.inProgress || !this.selectedPlan) {
      return;
    }

    if (this.stripeInitializationFailed || !this.stripeInitialized || !this.stripe || !this.mainStripeElement) {
      this.paymentError = '';
      this.initializeStripe();
      return;
    }

    this.cardholderForm.markAllAsTouched();
    this.invalidState.next(true);

    if (this.isCreditCardFormValid()) {
      this.inProgress = true;
      this.paymentError = '';

      this.stripe.confirmCardSetup(this.subscription.credentials.clientSecret,
        {
          payment_method: {
            card: this.mainStripeElement,
            billing_details: {
              address: {
                postal_code: this.cardholderForm.value.zip
              }
            },
          },
        }
      ).then((result) => {
        if (!this.inProgress) {
          return;
        }

        if (result.error) {
          const error = result.error;
          this.paymentError = error?.code === 'card_declined' ? 'We were unable to add your card. Please note that prepaid cards are not supported. If your card is not prepaid, ensure the zip code and CVV are entered correctly.' : (error?.message || 'An error occurred. Please refresh the page and try again.');
          this.analytics.trackEvent(AnalyticsEvent.CardAddedError, { message: typeof this.paymentError === 'string' ? this.paymentError : JSON.stringify(this.paymentError), userEmail: this.user.email });

          this.captureSubscriptionError(error, 'stripe_confirm_card_setup_failed', {
            errorCode: error?.code,
            errorType: error?.type,
            declineCode: error?.decline_code,
            cardBrand: result?.setupIntent?.payment_method_types?.[0]
          });

          this.inProgress = false;
        } else {
          const hasCreditCardOnFile = this.hasCreditCardOnFile;
          this.subscription.handleSubscription(this.subscription.getFilteredPrices(this.selectedPlan.prices), this.isPaymentMethodInvalid, result.setupIntent.payment_method)
            .subscribe((subscription: SubscriptionData) => {
              if (hasCreditCardOnFile) {
                this.analytics.trackEvent(AnalyticsEvent.SubscriptionPlanChanged, { plan: this.selectedPlan.name });
              } else {
                this.analytics.fireCreditCardAddedEvent();
              }
              this.inProgress = false;
              this.refreshData().subscribe(() => {
                this.refreshBilling();
                if (this.websiteTextingSelected) {
                  this.wtSubscriptionService.subscribe().subscribe();
                }
              });
            }, (error) => {
              this.paymentError = error?.toLowerCase?.()?.includes('coupon') ? 'Promotion code invalid. Please re-enter or remove code.' : `${error}`;
              this.inProgress = false;
              this.subscription.getBilling().subscribe({
                next: () => this.refreshData().subscribe(() => this.refreshBilling()),
                error: () => {}
              });
            });
        }
      }).catch((error) => {
        this.captureSubscriptionError(error, 'stripe_confirm_card_setup_exception', {
          errorMessage: error?.message,
          errorStack: error?.stack
        });

        const errorMessage = error?.message || error?.toString() || 'Unknown error';
        this.paymentError = error?.toLowerCase?.()?.includes('coupon') ? 'Promotion code invalid. Please re-enter or remove code.' : `An error occurred while processing your payment: ${errorMessage}. Please refresh the page and try again.`;
        this.inProgress = false;
        this.subscription.getBilling().subscribe({
          error: () => {}
        });
      });
    }
  }

  resubscribe() {
    if (!this.selectedPlan || this.inProgress) {
      return;
    }
    if (this.isPaymentMethodInvalid) {
      this.subscribe();
      return;
    }

    const hasCreditCardOnFile = this.hasCreditCardOnFile;
    this.inProgress = true;
    this.paymentError = '';

    this.subscription.handleSubscription(this.subscription.getFilteredPrices(this.selectedPlan.prices), this.isPaymentMethodInvalid)
      .subscribe({
        next: (subscription: SubscriptionData) => {
          if (hasCreditCardOnFile) {
            this.analytics.trackEvent(AnalyticsEvent.SubscriptionPlanChanged, { plan: this.selectedPlan.name });
          } else {
            this.analytics.trackEvent(AnalyticsEvent.CardAdded, { selectedPlan: this.selectedPlan.name });
          }
          this.subscription.created.next(true);
          this.inProgress = false;
          this.refreshData().subscribe(() => this.refreshBilling());
        },
        error: (error) => {
          this.captureSubscriptionError(error, 'resubscription_failed', {
            hadCreditCardOnFile: hasCreditCardOnFile,
            httpError: error
          });

          this.subscription.isPaymentMethodInvalid = true;
          this.paymentError = error?.toLowerCase?.()?.includes('coupon') ? 'Promotion code invalid. Please re-enter or remove code.' : `${error}`;
          this.inProgress = false;
          this.subscription.getBilling().subscribe({
            error: () => {}
          });
        }
      });
  }

  handlePlanChange() {
    this.resubscribe();
  }

  private captureSubscriptionError(error: any, context: string, additionalData?: any) {
    console.error(`Subscription Error [${context}]:`, error);

    if (CustomErrorHandler.isSentryEnabled) {
      Sentry.withScope((scope) => {
        scope.setContext('subscription', {
          context,
          selectedPlan: this.selectedPlan?.name,
          hasCreditCardOnFile: this.hasCreditCardOnFile,
          isTrialing: this.isTrialing,
          isCanceled: this.isCanceled,
          isPaymentMethodInvalid: this.isPaymentMethodInvalid,
          userId: this.user.userData?.id,
          businessId: this.activeBusiness?.id,
          ...additionalData
        });

        scope.setTag('error_type', 'subscription');
        scope.setTag('context', context);
        scope.setLevel('error');

        if (error instanceof Error) {
          Sentry.captureException(error);
        } else {
          Sentry.captureMessage(`Subscription error: ${error?.toString?.() || 'Unknown error'}`, 'error');
        }
      });
    }

    this.error.handleError(error);
  }

  handleCouponApplied() {
    // Invoice preview is automatically refreshed in the subscription service
    // after coupon is applied, no additional action needed here
  }

  handleCouponRemoved() {
    // Refresh invoice preview after coupon is removed
    if ((this.isUpgrade || this.isDowngrade) && !this.isTrialing && this.selectedPlan) {
      this.subscription.isLoadingInvoicePreview = true;
      const filteredPrices = this.subscription.getFilteredPrices(this.selectedPlan.prices);
      this.subscription.getInvoicePreview(filteredPrices)
        .subscribe(() => this.subscription.isLoadingInvoicePreview = false, () => this.subscription.isLoadingInvoicePreview = false);
    }
  }

  initializeStripe() {
    if (this.isExternalBillingProvider) {
      return;
    }

    if (this.stripeInitialized && this.stripe && this.stripeElements) {
      console.log('Stripe already initialized, skipping re-initialization');
      return;
    }

    const maxDomRetries = 5;
    let domRetryCount = 0;

    const waitForDom = () => {
      if (!this.cardElement?.nativeElement) {
        if (domRetryCount < maxDomRetries) {
          domRetryCount++;
          setTimeout(waitForDom, 200);
          return;
        } else {
          console.error('Card element not available after retries');
          this.handleStripeInitializationError('Unable to load payment form. Please refresh the page and try again.');
          return;
        }
      }
      this.loadStripeScript();
    };

    waitForDom();
  }

  private handleStripeInitializationError(errorMessage: string) {
    this.stripeInitializationFailed = true;
    this.stripeInitialized = false;
    this.isInitializingPaymentMethod = false;
    this.paymentError = errorMessage;
  }

  private loadStripeScript() {
    this.isInitializingPaymentMethod = true;
    this.stripeInitializationFailed = false;
    this.stripeInitialized = false;
    this.subscription.paymentMethodErrors = ['card-number', 'card-expiry', 'card-cvc'];
    const script = { src: 'https://js.stripe.com/v3/', name: 'stripe', };
    const maxRetries = 5;
    let retryCount = 0;

    const tryLoadScript = () => {
      this.scriptService.load(script)
        .subscribe({
          next: () => {
            try {
              if (typeof Stripe === 'undefined') {
                console.error('Stripe global not available after script load');
                if (retryCount < maxRetries) {
                  retryCount++;
                  console.log(`Retrying Stripe script load (${retryCount}/${maxRetries})...`);
                  setTimeout(tryLoadScript, 500 * retryCount);
                  return;
                } else {
                  this.captureSubscriptionError(
                    new Error('Stripe global not available after max retries'),
                    'stripe_script_load_failed',
                    { maxRetries }
                  );
                  this.handleStripeInitializationError('Unable to load payment processor. Please check your internet connection and refresh the page.');
                  return;
                }
              }

              if (!this.subscription.credentials?.publishableKey || !this.subscription.credentials?.clientSecret) {
                console.warn('Stripe credentials not available, fetching...');
                this.subscription.getBilling().subscribe({
                  next: () => {
                    if (this.subscription.credentials?.publishableKey && this.subscription.credentials?.clientSecret) {
                      this.refreshStripeFields();
                      this.stripe = Stripe(this.subscription.credentials.publishableKey);
                      this.stripeElements = this.stripe.elements({ clientSecret: this.subscription.credentials.clientSecret });
                      this.setupFields();
                    } else {
                      this.handleStripeInitializationError('Unable to initialize payment form. Please refresh the page and try again.');
                    }
                  },
                  error: () => {
                    this.handleStripeInitializationError('Unable to initialize payment form. Please refresh the page and try again.');
                  }
                });
                return;
              }

              this.refreshStripeFields();
              this.stripe = Stripe(this.subscription.credentials.publishableKey);
              this.stripeElements = this.stripe.elements({ clientSecret: this.subscription.credentials.clientSecret });
              this.setupFields();
            } catch (error) {
              this.captureSubscriptionError(error, 'stripe_initialization_error', {
                hasPublishableKey: !!this.subscription.credentials?.publishableKey,
                hasClientSecret: !!this.subscription.credentials?.clientSecret
              });
              this.handleStripeInitializationError('Unable to load payment form. Please refresh the page and try again.');
            }
          },
          error: (error) => {
            this.captureSubscriptionError(error, 'stripe_script_load_error', {
              scriptSrc: script.src,
              retryCount
            });
            this.handleStripeInitializationError('Unable to load payment form. Please refresh the page and try again.');
          }
        });
    };

    tryLoadScript();
  }

  clearForm() {
    this.invalidState.next(false);
    this.paymentError = '';
    this.cardholderForm.get('zip').reset();
    this.stripeInitializationFailed = false;
    this.stripeInitialized = false;
    this.stripeInitRetries = 0;
    this.isInitializingPaymentMethod = true;
    this.baseCssClasses = new Set<string>([FormCssClasses.Control]);
    clearTimeout(this.stripeInitTimeoutId);
  }

  redirectToDashboard() {
    this.router.navigateByUrl('');
  }

  closeSubscriptionModal() {
    this.modal.closeAll();
  }

  private refreshBilling() {
    this.subscription.getBilling().subscribe(() => {
      this.paymentError = '';
      this.inProgress = false;
    });
  }

  private setupFields() {
    this.stripeFieldSubscriptions.forEach(sub => sub.unsubscribe());
    this.stripeFieldSubscriptions = [];
    this.baseCssClasses = new Set<string>([FormCssClasses.Control]);

    const fieldStyle = {
      ...style,
      classes: {
        ...style.classes,
        base: [...this.baseCssClasses].join(' ')
      }
    };

    this.getStripeFields().forEach((inputFieldConfig: PaymentOptionField) => {
      const fieldElement = this.stripeElements.create(inputFieldConfig.customElement, fieldStyle);
      const fieldErrorElement = document.getElementById(`${inputFieldConfig.id}-error`);

      fieldElement.mount(`#${inputFieldConfig.id}`);
      if (inputFieldConfig.mainElement) {
        this.mainStripeElement = fieldElement;
        fieldElement.on('mounted', () => {
          this.mainStripeElement = fieldElement;
        })
      }
      this.stripeElementInstances.push(fieldElement);
      fieldErrorElement.textContent = `${this.translate.instant(inputFieldConfig.label)} is required`;

      this.handleFieldOnChange(fieldElement, inputFieldConfig, fieldErrorElement);
      this.handleFieldOnFocus(fieldElement, inputFieldConfig);

      this.stripeFieldSubscriptions.push(
        this.invalidState.subscribe((isInvalid: boolean) => {
          if (isInvalid) {
            this.baseCssClasses.add(FormCssClasses.Touched);
          } else {
            this.baseCssClasses.delete(FormCssClasses.Touched);
            this.baseCssClasses.delete(FormCssClasses.Dirty);
          }
          fieldElement.update({ classes: { base: [...this.baseCssClasses].join(' ') } });
        })
      );
    });
    this.mainStripeElement.on('ready', () => {
      this.stripeInitialized = true;
      this.stripeInitializationFailed = false;
      this.isInitializingPaymentMethod = false;
      this.paymentError = '';
      clearTimeout(this.stripeInitTimeoutId);
    });
    this.stripeInitTimeoutId = setTimeout(() => {
      if (!this.stripeInitialized && !this.stripeInitializationFailed) {
        if (this.stripeInitRetries < 2) {
          this.stripeInitRetries++;
          this.refreshStripeFields();
          this.initializeStripe();
        } else {
          this.handleStripeInitializationError('Unable to load payment form. Please refresh the page and try again.');
        }
      }
    }, 15000);
  }

  private handleFieldOnChange(fieldElement: any, inputFieldConfig: PaymentOptionField, fieldErrorElement: any) {
    fieldElement.on('change', (event) => {
      if (!event.empty) {
        this.baseCssClasses.add(FormCssClasses.Dirty);
      } else {
        this.baseCssClasses.delete(FormCssClasses.Dirty);
      }
      if (!event.complete) {
        this.baseCssClasses.add(FormCssClasses.Invalid);
        this.baseCssClasses.delete(FormCssClasses.Valid);
      } else {
        this.baseCssClasses.delete(FormCssClasses.Invalid);
        this.baseCssClasses.add(FormCssClasses.Valid);
      }
      fieldElement.update({ classes: { base: [...this.baseCssClasses].join(' ') } });

      if (event.error) {
        fieldErrorElement.textContent = event.error.message;
        this.subscription.paymentMethodErrors.push(inputFieldConfig.id);
      } else {
        fieldErrorElement.textContent = '';
        this.subscription.paymentMethodErrors = this.subscription.paymentMethodErrors.filter(error => error !== inputFieldConfig.id);
      }
    });
  }

  private handleFieldOnFocus(fieldElement: any, inputFieldConfig: PaymentOptionField) {
    fieldElement.on('focus', (event) => {
      this.subscription.paymentMethodErrors = this.subscription.paymentMethodErrors.filter(error => error !== inputFieldConfig.id);
      this.baseCssClasses.add(FormCssClasses.Touched);
      if (!this.baseCssClasses.has(FormCssClasses.Dirty)) {
        this.baseCssClasses.add(FormCssClasses.Invalid);
      } else {
        this.baseCssClasses.delete(FormCssClasses.Invalid);
      }
      fieldElement.update({ classes: { base: [...this.baseCssClasses].join(' ') } });
    });
  }

  private isCreditCardFormValid(): boolean {
    return this.subscription.paymentMethodErrors?.length === 0 && !this.inProgress && this.cardholderForm.valid;
  }

  private refreshStripeFields() {
    this.stripeElementInstances.forEach((element: any) => {
      try {
        element.unmount();
      } catch (error) {
        console.error('Error unmounting Stripe element:', error);
      }
    });
    this.stripeElementInstances = [];
    this.mainStripeElement = null;
    this.stripeFieldSubscriptions.forEach(sub => sub.unsubscribe());
    this.stripeFieldSubscriptions = [];
    this.stripeInitialized = false;
  }

  private getStripeFields(): PaymentOptionField[] {
    return [
      {
        id: 'card-number',
        label: 'SUBSCRIPTION.CARD_NUMBER_LABEL',
        customElement: StripeCustomElement.CardNumber,
        mainElement: true,
        cardNumberField: true,
        class: 'rosie-form-control form-control',
      },
      {
        id: 'card-expiry',
        label: 'SUBSCRIPTION.EXPIRY_DATE_LABEL',
        customElement: StripeCustomElement.CardExpiry,
        specificErrorMsg: true,
        class: 'rosie-form-control form-control'
      },
      {
        id: 'card-cvc',
        label: 'SUBSCRIPTION.CVC_LABEL',
        customElement: StripeCustomElement.CardCvc,
        class: 'rosie-form-control form-control'
      }
    ];
  }

  private refreshData() {
    this.subscription.close.next(true);
    const user = this.user.userData;
    user.subscription.isCreditCardAdded = true;
    this.user.refreshUser(user);
    return this.user.getUser();
  }
}


export enum FormCssClasses {
  Control = 'form-control',
  Focus = 'ng-focus',
  Dirty = 'ng-dirty',
  Invalid = 'ng-invalid',
  Valid = 'ng-valid',
  Touched = 'ng-touched',
  Empty = 'ng-empty'
}

export enum PaymentCssClasses {
  CCIcon = 'rosie-credit-card-icon'
}

export enum StripeCustomElement {
  CardNumber = 'cardNumber',
  CardExpiry = 'cardExpiry',
  CardCvc = 'cardCvc',
}

export interface PaymentOptionField {
  id: string;
  label: string;
  customElement?: string;
  mainElement?: boolean;
  class?: string;
  cardholderName?: boolean;
  ownCardholderName?: boolean;
  cardNumberField?: boolean;
  specificErrorMsg?: boolean;
}


export const style = {
  placeholder: '',
  classes: {
    focus: FormCssClasses.Focus,
    invalid: FormCssClasses.Invalid,
    empty: `${FormCssClasses.Empty} ${FormCssClasses.Invalid}`,
    base: FormCssClasses.Control
  },
  style: {
    invalid: {
      color: '#000',
      iconColor: '#000',
      lineHeight: '20px'
    },
    complete: {
      lineHeight: '20px'
    },
    base: {
      lineHeight: '50px',
      ':focus': {
        lineHeight: '20px'
      }
    }
  }
};
